import { useParams } from 'react-router-dom';
import { useKernelApi } from 'infrastructure/api';
import useLeadService, { RESOURCE_URL } from 'domain/sales/lead/shared/leadService';
import { ACCOUNT_TYPE } from 'domain/sales/account/shared/input/AccountInput';
import convertLeadValidation from 'domain/sales/lead/Convert/convertLeadValidation';
import { useCrudService } from 'framework/crud';
import { DEFAULT_ERROR_MESSAGE, COMMON_ERROR_SOURCE } from 'framework/form';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';

const getModifiedDataForLeadConvert = (data: any) => {
  const result = { ...data };

  switch (result.accountType) {
    case ACCOUNT_TYPE.NEW:
      result.accountId = null;
      break;
    case ACCOUNT_TYPE.EXISTING:
      result.accountId = result.existingAccountName;
      break;
    default:
  }

  if (result.createOpportunity) {
    delete result.opportunityName;
    delete result.closeDate;
  }

  // delete properties that aren't needed for the request
  delete result.createOpportunity;
  delete result.accountType;
  delete result.existingAccountName;

  return result;
};

/**
 * Convert Lead service.
 */

const useConvertLeadService = () => {
  const { getById } = useLeadService();
  const { id: leadId } = useParams<Dictionary<string>>();
  const { sendPostRequest } = useKernelApi();
  const { addMessage } = useMessageService();
  const { invalidateCache } = useCrudService({
    singleResourceUrl: `${RESOURCE_URL}/:id`,
    listResourceUrl: RESOURCE_URL,
    useService: useKernelApi,
  });

  const toErrorString = (payload: any): string => {
    const errors = payload?.errors;
    const message = payload?.message ?? payload?.error;

    if (errors && typeof errors === 'object' && !Array.isArray(errors) && errors[COMMON_ERROR_SOURCE] != null) {
      return String(errors[COMMON_ERROR_SOURCE]);
    }

    if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
      const lines: string[] = [];
      Object.entries(errors).forEach(([field, val]) => {
        if (Array.isArray(val)) val.forEach(v => lines.push(`${field}: ${String(v)}`));
        else lines.push(`${field}: ${String(val)}`);
      });
      if (lines.length) return lines.join('\n');
    }

    if (errors != null) return Array.isArray(errors) ? errors.map(String).join('\n') : String(errors);

    if (message != null) return String(message);

    return DEFAULT_ERROR_MESSAGE;
  };

  const convertLead = async (id: StringOrNumber, data: any) => {
    const modifiedData = getModifiedDataForLeadConvert(data);
    const url = `${RESOURCE_URL}/${id}/convert`;

    try {
      const response = await sendPostRequest(url, modifiedData);

      if (invalidateCache) {
        invalidateCache();
      }

      return response.json();
    } catch (error: any) {
      let msg;

      try {
        const payload = error?.json ? await error.json() : error;
        msg = toErrorString(payload);
      } catch {
        msg = error?.message || DEFAULT_ERROR_MESSAGE;
      }

      addMessage(new ErrorMessage(msg));
      throw msg;
    }
  };

  const getConvertValidationSchema = () => Promise.resolve(convertLeadValidation);

  const getInitialData = async () => {
    const data = await getById(leadId!);

    return data;
  };

  return {
    convertLead,
    getInitialData,
    getConvertValidationSchema,
  };
};

export default useConvertLeadService;
